import React, { useState, useEffect, useCallback } from 'react';
import { InsightsView, IdentityService, TeamService, useAuth, UserAbility, MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle } from '@medprompt/shared';
import { InsightsService, sharedApiClient } from '../fetcher';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';

const InsightsViewController = ({ isSidebarExpanded }) => {
  const [insightsService] = useState(() => new InsightsService(sharedApiClient));
  const [identityService] = useState(() => new IdentityService());
  const [teamService] = useState(() => new TeamService());
  const [userIdToName, setUserIdToName] = useState({});
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [currentProcessorState, setCurrentProcessorState] = useState(null);
  const [currentDocumentsAssigned, setCurrentDocumentsAssigned] = useState(null);
  const [currentTasksState, setCurrentTasksState] = useState(null);
  const [currentConsultationsState, setCurrentConsultationsState] = useState(null);
  const { user } = useAuth();
  const hasInsightsReadAll = user?.authorities?.includes(UserAbility.INSIGHTS_READ_ALL.key);
  const hasDocsRead = !!user?.authorities?.includes(UserAbility.DOCS_READ.key);
  const hasTaskRead = !!user?.authorities?.includes(UserAbility.TASK_READ.key);
  const hasConsultRead = !!user?.authorities?.includes(UserAbility.CONSULT_READ.key);

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.insights);
  }, []);

  // Fetch active clinic users on component mount
  useEffect(() => {
    identityService.fetchActiveClinicUsers()
      .then((users) => {
        // Parse List<UserData> response to create userId -> name mapping
        const mapping = {};
        users.forEach(user => {
          mapping[user.userId] = user.name;
        });
        setUserIdToName(mapping);
        setActiveUsers(users);
      })
      .catch((error) => {
        console.error('Failed to fetch clinic users:', error);
        // Continue with empty mapping
        setUserIdToName({});
        setActiveUsers([]);
      });
  }, [identityService]);

  // Fetch current processor state on component mount (independent of date range)
  useEffect(() => {
    insightsService.fetchCurrentProcessorState()
      .then((state) => {
        setCurrentProcessorState(state);
      })
      .catch((error) => {
        console.error('Failed to fetch current processor state:', error);
        setCurrentProcessorState(null);
      });
  }, [insightsService]);

  useEffect(() => {
    insightsService.fetchCurrentDocumentsAssigned()
      .then((summary) => {
        setCurrentDocumentsAssigned(summary?.documentsAssigned || []);
      })
      .catch((error) => {
        console.error('Failed to fetch current document assignments:', error);
        setCurrentDocumentsAssigned([]);
      });
  }, [insightsService]);

  // Fetch current tasks state on component mount (independent of date range)
  useEffect(() => {
    insightsService.fetchCurrentTasksState()
      .then((state) => {
        setCurrentTasksState(state);
      })
      .catch((error) => {
        console.error('Failed to fetch current tasks state:', error);
        setCurrentTasksState(null);
      });
  }, [insightsService]);

  useEffect(() => {
    insightsService.fetchCurrentConsultationsState()
      .then((state) => {
        setCurrentConsultationsState(state);
      })
      .catch((error) => {
        console.error('Failed to fetch current consultations state:', error);
        setCurrentConsultationsState(null);
      });
  }, [insightsService]);

  // Fetch teams on component mount
  useEffect(() => {
    teamService.fetchTeams()
      .then((teamsData) => {
        setTeams(teamsData);
      })
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, [teamService]);

  // Create a fetch function that matches the expected interface
  const fetchInsights = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    // For the regular app, we ignore clinicId since it's contextual to the current user
    try {
      const data = await insightsService.fetchInsights(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  // Create a fetch function for task insights
  const fetchTasksSnapshots = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    // For the regular app, we ignore clinicId since it's contextual to the current user
    try {
      const data = await insightsService.fetchTasksSnapshots(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  // Create a fetch function for document review insights
  const fetchDocumentReviewInsights = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    try {
      const data = await insightsService.fetchDocumentReviewInsights(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  // Create a fetch function for documents review aggregate
  const fetchDocumentsReviewAggregate = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    // For the regular app, we ignore clinicId since it's contextual to the current user
    try {
      const data = await insightsService.fetchDocumentsReviewAggregate(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  const fetchConsultationsSnapshots = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    try {
      const data = await insightsService.fetchConsultationsSnapshots(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  const fetchConsultationsReviewSummary = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    try {
      const data = await insightsService.fetchConsultationsReviewSummary(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  const fetchConsultationsReviewAggregate = useCallback(async (from, to, clinicId, successCallback, errorCallback) => {
    try {
      const data = await insightsService.fetchConsultationsReviewAggregate(from, to);
      successCallback(data);
    } catch (error) {
      errorCallback(error);
    }
  }, [insightsService]);

  // Handle document click from scatter plot
  const handleDocumentClick = useCallback((documentId) => {
    if (documentId) {
      setSelectedDocumentId(documentId);
      setIsModalOpen(true);
    }
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  return (
    <>
      <InsightsView
        title={MAIN_TAB_PAGE_LABEL.insights}
        subtitle={user && user.name ? `Viewing ${user.name}'s insights` : undefined}
        titleActions={<HelpButton pageId={HELP_PAGE_IDS.insights} />}
        fetchInsights={fetchInsights}
        fetchTasksSnapshots={fetchTasksSnapshots}
        fetchConsultationsSnapshots={fetchConsultationsSnapshots}
        fetchDocumentReviewInsights={fetchDocumentReviewInsights}
        fetchDocumentsReviewAggregate={fetchDocumentsReviewAggregate}
        fetchConsultationsReviewSummary={fetchConsultationsReviewSummary}
        fetchConsultationsReviewAggregate={fetchConsultationsReviewAggregate}
        currentProcessorState={currentProcessorState}
        currentDocumentsAssigned={currentDocumentsAssigned}
        currentTasksState={currentTasksState}
        currentConsultationsState={currentConsultationsState}
        hasDocsRead={hasDocsRead}
        hasTaskRead={hasTaskRead}
        hasConsultRead={hasConsultRead}
        userIdToName={userIdToName}
        activeUsers={activeUsers}
        teams={teams}
        showUserCharts={hasInsightsReadAll}
        onDocumentClick={handleDocumentClick}
        isSidebarExpanded={isSidebarExpanded}
      />
      <DocumentPreviewModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        documentId={selectedDocumentId}
        activeUsers={activeUsers}
        teams={teams}
        taskId={null}
      />
    </>
  );
};

export default InsightsViewController;

